import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

const PYTHON_CODE_LINE_RE =
  /^(print\(|def |import |from |for |while |if |elif |else:|class |return |[a-z_][\w]*\s*[=([])/i;

function looksLikePythonCodeLine(line: string): boolean {
  const trimmed = line.trim();
  if (!trimmed) return false;
  if (PYTHON_CODE_LINE_RE.test(trimmed)) return true;
  return /^\s{2,}\S/.test(line);
}

/** Wrap bare Python-looking lines in fenced blocks when authors omit backticks. */
export function enhanceBareCodeBlocks(text: string): string {
  if (text.includes("```")) return text;
  const lines = text.split("\n");
  const result: string[] = [];
  let codeRun: string[] = [];

  const flushCode = () => {
    if (codeRun.length === 0) return;
    result.push("```");
    result.push(...codeRun);
    result.push("```");
    codeRun = [];
  };

  for (const line of lines) {
    if (looksLikePythonCodeLine(line)) {
      codeRun.push(line);
      continue;
    }
    flushCode();
    result.push(line);
  }
  flushCode();
  return result.join("\n");
}

/** Preserve single newlines as markdown hard breaks outside fenced code blocks. */
export function normalizeQuizMarkdown(text: string): string {
  const enhanced = enhanceBareCodeBlocks(text);
  const lines = enhanced.split("\n");
  const out: string[] = [];
  let inFence = false;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]!;
    if (/^\s*```/.test(line)) {
      inFence = !inFence;
      out.push(line);
      continue;
    }
    if (inFence) {
      out.push(line);
      continue;
    }
    if (!line.trim()) {
      out.push("");
      continue;
    }
    const next = lines[i + 1];
    const nextStartsFence = next !== undefined && /^\s*```/.test(next);
    if (next !== undefined && next.trim() !== "" && !nextStartsFence) {
      out.push(`${line}  `);
    } else {
      out.push(line);
    }
  }
  return out.join("\n");
}

export interface QuizRichTextProps {
  readonly text: string;
  readonly className?: string;
}

export function QuizRichText({ text, className }: QuizRichTextProps) {
  if (!text.trim()) return null;
  const source = normalizeQuizMarkdown(text);

  return (
    <div className={["quiz-rich-text", "ide-markdown", className].filter(Boolean).join(" ")}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          a: ({ node: _node, ...props }) => <a {...props} target="_blank" rel="noreferrer" />,
          pre: ({ node: _node, ...props }) => <pre {...props} />,
          code: ({ node: _node, className: codeClass, children, ...props }) => {
            const isBlock = Boolean(codeClass);
            if (isBlock) {
              return (
                <code className={codeClass} {...props}>
                  {children}
                </code>
              );
            }
            return (
              <code className="quiz-inline-code" {...props}>
                {children}
              </code>
            );
          },
        }}
      >
        {source}
      </ReactMarkdown>
    </div>
  );
}
